import QRCode from 'qrcode';
import { labelTargetUrl } from '../lib/appUrl';
import { getCompanyName } from '../lib/tenant';
import {
  qrPixelWidth,
  qrPhysicalWidthIn,
  labelDetailText,
  getSubject,
  LABEL_FORMATS,
  FORMAT_ORDER,
} from '../lib/labelFormats';

// ---- Locked physical constants (confirmed Session 47) ----
export const M5_USABLE_WIDTH_IN = 52.5;   // usable print width on the 54" roll
export const M5_ROLL_WIDTH_IN = 54;       // full roll width, for reference/display only
export const M5_GUTTER_IN = 0.25;         // gap between labels, both axes
// Session 75. Was `M5_LABEL_HEIGHT_IN = 1`, fixed, because both size profiles
// were one inch tall. The gate's ladder adds four formats that are not, so tile
// height now comes from the format.

// Derived from the ladder rather than hand-listed, so a format added to the
// gate's Canon destinations appears here without a second edit.
//
// Session 76: CanonM5Panel.js, the only consumer of the {id, label, widthIn}
// shape, was deleted when BatchPrintModal absorbed the Canon destination. The
// shape is kept because computeM5Layout reads widthIn/heightIn from it.
export const M5_SIZE_PROFILES = Object.fromEntries(
  FORMAT_ORDER
    .filter(id => LABEL_FORMATS[id].destinations.includes('canon'))
    .map(id => {
      const f = LABEL_FORMATS[id];
      return [id, { id, label: `${f.label}`, widthIn: f.widthIn, heightIn: f.heightIn }];
    })
);

/**
 * Computes the tiling layout for a given size profile and item count.
 * across/rows use a floor/ceil on the confirmed 52.5" usable width and 0.25" gutter.
 * NOTE: these are the real computed values — they will differ from the Session 45
 * mockup's hardcoded demo numbers (21/78% for 2x1, 14/95% for 3x1), which were
 * placeholder figures, not derived from this formula.
 *
 * Session 75: widthIn is the dimension that tiles across the roll, and it is
 * always the FIRST number in the format's label. The gate's own figures - 16
 * across for 3x5, 6 for 8.5x11, 2 at 24in - are asserted in labelFormats.test.js
 * precisely because swapping a pair changes vinyl consumption silently.
 */
export function computeM5Layout(sizeId, itemCount) {
  const profile = M5_SIZE_PROFILES[sizeId];
  if (!profile || !itemCount || itemCount < 1) {
    return { across: 0, rows: 0, rowWidthIn: 0, lengthIn: 0, lengthFt: 0, fillPercent: 0 };
  }

  const across = Math.max(1, Math.floor(M5_USABLE_WIDTH_IN / (profile.widthIn + M5_GUTTER_IN)));
  const rows = Math.ceil(itemCount / across);
  const rowWidthIn = across * profile.widthIn + (across - 1) * M5_GUTTER_IN;
  const lengthIn = rows * (profile.heightIn + M5_GUTTER_IN);
  const lengthFt = lengthIn / 12;
  const fillPercent = Math.min(100, (rowWidthIn / M5_USABLE_WIDTH_IN) * 100);

  return { across, rows, rowWidthIn, lengthIn, lengthFt, fillPercent };
}

// #321. The Canon path is the one this matters most on: it is the only
// destination that prints the large formats, where a 256px code across a 22in
// tile is roughly 12 dots per inch.
async function generateQRDataURL(url, formatId = '2x1') {
  try {
    return await QRCode.toDataURL(url, {
      errorCorrectionLevel: 'M',
      margin: 1,
      width: qrPixelWidth(formatId),
      color: { dark: '#000000', light: '#ffffff' },
    });
  } catch (e) {
    console.error('QR generation failed:', e);
    return null;
  }
}

// #113, Session 77. What gets drawn where the QR should have been.
//
// generateQRDataURL catches its own failure and returns null; drawLabelTile then
// skipped doc.addImage and drew the text lines as normal. The result was a tile
// that looks finished, carries the right name and SKU, and cannot be scanned -
// on a sheet of vinyl that gets laminated and stuck to a rack. The failure was
// invisible until someone in the warehouse pointed a phone at it.
//
// Vinyl is the reason this is a marked box rather than a skipped tile or an
// aborted export: the batch is already paid for in material, so the useful
// outcome is a sheet where the bad tiles are obvious before it is cut, not a
// run that refuses to produce anything. Same reasoning as callout G - partial
// failure is the normal case in batch printing, and the remedy is to name it.
function drawQRFailureMark(doc, x, y, size, monochrome = false) {
  // Session 78a: red is a colour a thermal printer does not have. It has one
  // ink and one dot size, so #C62828 arrives as a dithered grey smear that
  // reads as a printing fault rather than as a deliberate warning mark.
  const mark = monochrome ? [0, 0, 0] : [198, 40, 40];
  doc.setDrawColor(...mark);
  doc.setLineWidth(Math.max(0.008, size * 0.02));
  doc.setLineDashPattern([], 0);
  doc.rect(x, y, size, size);
  // A cross, so it reads as "not a code" at a glance rather than as an empty box
  // that might be a design choice.
  doc.line(x, y, x + size, y + size);
  doc.line(x + size, y, x, y + size);

  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...mark);
  // Scales with the tile - the same problem the portrait branch solves for its
  // text - so the wall label does not carry a 5pt warning.
  doc.setFontSize(Math.max(4, Math.min(14, size * 72 * 0.16)));
  doc.text('QR FAILED', x + size / 2, y + size / 2, { align: 'center', baseline: 'middle' });
  doc.setTextColor(0, 0, 0);
}

function truncateToWidth(doc, text, maxWidthIn) {
  if (!text) return '';
  if (doc.getTextWidth(text) <= maxWidthIn) return text;
  let t = text;
  while (t.length > 1 && doc.getTextWidth(t + '…') > maxWidthIn) {
    t = t.slice(0, -1);
  }
  return t + '…';
}

// Session 78a: exported, and given a `cutGuide` switch.
//
// This function is the whole label layout - QR sizing off the short edge, the
// landscape/portrait branch, the truncation, the QR-failure box - and it was
// private because the Canon roll was the only thing that drew a tile. The
// Brother path now draws one too (see labelPdf.js for why the browser-print
// path could not be repaired in place), and it draws the SAME tile deliberately:
// a label that means the same thing should not be laid out twice.
//
// The two destinations disagree about exactly two things, and both are
// properties of the MACHINE rather than of the label: a Brother arrives already
// cut (no dashed guide) and prints one ink with no halftone (no grey ramp).
// Everything about what the label MEANS is shared.
export function drawLabelTile(doc, x, y, widthIn, heightIn, item, branchName, qrDataUrl, sizeId, subjectType = 'inventory', options = {}) {
  const { cutGuide = true, monochrome = false } = options;

  // Session 78a, found on real tape. A thermal transfer printer has ONE ink and
  // no halftone: every pixel is a dot or it is nothing. So the grey ramp this
  // tile uses to build hierarchy - 60/60/60 for the detail line, 90/90/90 for
  // the branch, 140/140/140 for the tenant - has to be dithered, and dithered
  // 5pt type at the PT-E560BT's resolution is mush. On the first correctly
  // sized label off the machine, the item name was crisp and "Dallas" and
  // "D&R Signs" were illegible. The QR survived because it was already pure
  // black.
  //
  // On vinyl none of this applies: the Canon is a full-colour inkjet and the
  // grey ramp is doing real work there. So it is an option, not a correction.
  //
  // The floor is a second, smaller half of the same finding: thin strokes at
  // 5pt have too few dots to survive even in pure black, and this tile had
  // room to spare - four text rows sit on 0.17in centres, which is about 12pt
  // of leading carrying 5pt type.
  const ink = monochrome ? (r, g, b) => doc.setTextColor(0, 0, 0)
                         : (r, g, b) => doc.setTextColor(r, g, b);
  const minPt = monochrome ? (s) => Math.max(6, s) : (s) => s;

  const detail = labelDetailText(subjectType, item);
  // cut guide — light dashed border, matches the laminate-then-cut workflow.
  // Off for a Brother label: it arrives already cut to shape, so the guide
  // would print as a decorative rectangle around the artwork.
  if (cutGuide) {
    doc.setDrawColor(190, 190, 190);
    doc.setLineWidth(0.006);
    doc.setLineDashPattern([0.04, 0.04], 0);
    doc.rect(x, y, widthIn, heightIn);
    doc.setLineDashPattern([], 0);
  }

  const pad = Math.max(0.06, heightIn * 0.06);
  const portrait = heightIn > widthIn;

  // Session 75. Two layouts, chosen by the tile's own proportion rather than by
  // a format id, so a format added later needs no branch here.
  //
  // Landscape (2x1, 3x1) keeps the Session 47 layout: QR left, text right.
  // Portrait (3x5, 4x6, 8.5x11, 24x30) stacks - a QR sized off the long edge
  // would be wider than the label. This is what the gate's ladder drawing
  // shows: on the wall label the code nearly fills the width.
  const qrSize = portrait
    ? Math.min(qrPhysicalWidthIn(sizeId), widthIn - pad * 2)
    : heightIn - pad * 2;

  if (portrait) {
    const qrX = x + (widthIn - qrSize) / 2;
    if (qrDataUrl) {
      doc.addImage(qrDataUrl, 'PNG', qrX, y + pad, qrSize, qrSize);
    } else {
      drawQRFailureMark(doc, qrX, y + pad, qrSize, monochrome);
    }

    const textTop = y + pad + qrSize;
    const textWidthIn = widthIn - pad * 2;
    const remaining = heightIn - (pad + qrSize) - pad;
    if (remaining <= 0.1) return;

    // Type scales with the tile so a 24in label is not set in 7pt. Four lines
    // share the space below the code.
    const line = remaining / 4.6;
    const pt = inches => Math.max(4, inches * 72);
    let cursor = textTop + line;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(pt(line * 0.78));
    doc.setTextColor(0, 0, 0);
    doc.text(truncateToWidth(doc, item?.name || '—', textWidthIn), x + pad, cursor);

    doc.setFont('helvetica', 'normal');
    cursor += line;
    doc.setFontSize(minPt(pt(line * 0.62)));
    ink(60, 60, 60);
    doc.text(truncateToWidth(doc, detail, textWidthIn), x + pad, cursor);

    cursor += line * 0.9;
    doc.setFontSize(minPt(pt(line * 0.55)));
    ink(90, 90, 90);
    doc.text(truncateToWidth(doc, branchName || '—', textWidthIn), x + pad, cursor);

    cursor += line * 0.8;
    doc.setFontSize(minPt(pt(line * 0.45)));
    ink(140, 140, 140);
    doc.text(truncateToWidth(doc, getCompanyName(), textWidthIn), x + pad, cursor);
    return;
  }

  if (qrDataUrl) {
    doc.addImage(qrDataUrl, 'PNG', x + pad, y + pad, qrSize, qrSize);
  } else {
    drawQRFailureMark(doc, x + pad, y + pad, qrSize, monochrome);
  }

  const textX = x + pad + qrSize + 0.09;
  const textWidthIn = widthIn - (pad + qrSize + 0.09) - pad;
  if (textWidthIn <= 0.15) return; // too narrow (shouldn't happen at 2"/3" widths, guards against future profiles)

  doc.setFont('helvetica', 'bold');
  // Session 78a: fractions of the tile height, not absolute inches.
  //
  // These four rows were 0.24 / 0.42 / 0.59 / 0.76 - correct, and silently
  // dependent on the tile being exactly one inch tall, which every landscape
  // tile was until the Brother page became the tape's ink band (~0.71in). At
  // that height the last two rows land BELOW the label and print nowhere. The
  // fractions are those same numbers over 1.00, so a one-inch tile - the Canon
  // 2x1 and 3x1 - is unchanged to the thousandth.
  const row = f => y + heightIn * f;

  doc.setFontSize(minPt(7));
  ink(0, 0, 0);
  doc.text(truncateToWidth(doc, item?.name || '—', textWidthIn), textX, row(0.24));

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(minPt(6));
  ink(60, 60, 60);
  doc.text(truncateToWidth(doc, detail, textWidthIn), textX, row(0.42));

  doc.setFontSize(minPt(5.5));
  ink(90, 90, 90);
  doc.text(truncateToWidth(doc, branchName || '—', textWidthIn), textX, row(0.59));

  doc.setFontSize(minPt(5));
  ink(140, 140, 140);
  doc.text(truncateToWidth(doc, getCompanyName(), textWidthIn), textX, row(0.76));
}

/**
 * Generates and downloads the tiled Canon M5 PDF for a batch of records.
 *
 * items: one entry PER LABEL, not per record — the caller expands copies before
 *        calling, because a tile is a physical rectangle and two copies are two
 *        rectangles. Inventory records are { id, name, sku, uom }; tools are
 *        { id, name, serial_number, station_truck }.
 * options.subjectType: 'inventory' (default) or 'tool'. Session 76 — this file
 *        previously hardcoded the inventory scan route and the sku/uom detail
 *        line for every caller, which was harmless only while the sole caller
 *        was an inventory page.
 */
export async function exportCanonM5PDF(items, branchName, sizeId, options = {}) {
  if (!items || items.length === 0) return { success: false, error: 'no_items' };

  const subjectType = options.subjectType || 'inventory';
  const subject = getSubject(subjectType);
  if (!subject) return { success: false, error: 'invalid_subject' };

  const profile = M5_SIZE_PROFILES[sizeId];
  if (!profile) return { success: false, error: 'invalid_profile' };

  const layout = computeM5Layout(sizeId, items.length);
  if (layout.across < 1) return { success: false, error: 'layout_failed' };

  let doc;
  try {
    const { jsPDF } = await import('jspdf');
    doc = new jsPDF({
      orientation: layout.rowWidthIn >= layout.lengthIn ? 'l' : 'p',
      unit: 'in',
      format: [layout.rowWidthIn, layout.lengthIn],
    });
  } catch (e) {
    console.error('jsPDF init failed:', e);
    return { success: false, error: 'pdf_init_failed' };
  }

  // One QR per DISTINCT record, not per tile. A batch of 40 copies of one item
  // is one code generated once and drawn 40 times — expanding copies before
  // this point must not multiply the QR work with it.
  const qrCache = new Map();
  await Promise.all(
    [...new Set(items.map(item => item.id))].map(async id => {
      qrCache.set(id, await generateQRDataURL(labelTargetUrl(subject.route(id)), sizeId));
    })
  );

  // #113. Counted over TILES, not over records, because that is the number a
  // person has to act on - a failed record with 12 copies is 12 unusable
  // stickers. The record ids travel with it so a caller can name them rather
  // than making someone hunt the sheet for the marked ones.
  const failedIds = [...qrCache.entries()].filter(([, url]) => !url).map(([id]) => id);
  const qrFailures = items.filter(item => !qrCache.get(item.id)).length;

  items.forEach((item, i) => {
    const col = i % layout.across;
    const row = Math.floor(i / layout.across);
    const x = col * (profile.widthIn + M5_GUTTER_IN);
    const y = row * (profile.heightIn + M5_GUTTER_IN);
    drawLabelTile(doc, x, y, profile.widthIn, profile.heightIn, item, branchName, qrCache.get(item.id), sizeId, subjectType);
  });

  const dateStr = new Date().toISOString().slice(0, 10);
  const filename = `stede-labels-${subjectType}-canon-m5-${sizeId}-${dateStr}.pdf`;

  try {
    doc.save(filename);
  } catch (e) {
    console.error('PDF save failed:', e);
    return { success: false, error: 'pdf_save_failed' };
  }

  return {
    success: true,
    totalLabels: items.length,
    qrFailures,
    failedIds,
    sizeId,
    across: layout.across,
    rows: layout.rows,
    lengthIn: layout.lengthIn,
    lengthFt: layout.lengthFt,
    printedAt: new Date().toISOString(),
  };
}